/**
 * CampaignKanban — Board estilo Trello para campañas publicitarias
 * Columnas: Borrador → Programado → Enviando → Publicado → Analítica
 * Drag & Drop visual, launch en un clic, IA integrada.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Send, RefreshCw, Zap, Globe, MessageCircle,
  Mail, BarChart3, Clock, CheckCircle2, Loader2,
  AlertTriangle, Sparkles, ChevronRight, Eye, X,
  Instagram, Play
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { id: 'draft',     label: 'Borradores',  color: 'border-slate-500/30',  bg: 'bg-slate-500/5',  icon: Clock,        statuses: ['draft'] },
  { id: 'scheduled', label: 'Programado',  color: 'border-blue-500/30',   bg: 'bg-blue-500/5',   icon: Clock,        statuses: ['scheduled'] },
  { id: 'sending',   label: 'Enviando',    color: 'border-amber-500/30',  bg: 'bg-amber-500/5',  icon: Loader2,      statuses: ['sending'] },
  { id: 'sent',      label: 'Publicado',   color: 'border-emerald-500/30',bg: 'bg-emerald-500/5',icon: CheckCircle2, statuses: ['sent', 'published'] },
];

const CHANNEL_CONFIG = {
  whatsapp:  { icon: MessageCircle, label: 'WhatsApp',  color: 'text-emerald-400',  bg: 'bg-emerald-500/15' },
  email:     { icon: Mail,          label: 'Email',     color: 'text-blue-400',     bg: 'bg-blue-500/15' },
  instagram: { icon: Instagram,     label: 'Instagram', color: 'text-pink-400',     bg: 'bg-pink-500/15' },
  tiktok:    { icon: Play,          label: 'TikTok',    color: 'text-slate-300',    bg: 'bg-slate-500/15' },
  social:    { icon: Globe,         label: 'Redes',     color: 'text-primary',      bg: 'bg-primary/15' },
};

/* ── Campaign card ─────────────────────────────────────────────────────────── */
function CampaignCard({ camp, onLaunch, onView, onRefresh }) {
  const ch = CHANNEL_CONFIG[camp.channel] || CHANNEL_CONFIG.social;
  const ChIcon = ch.icon;
  const sending = camp.status === 'sending';
  const sent = ['sent', 'published'].includes(camp.status);

  return (
    <div
      className="p-4 rounded-2xl bg-card border border-border-subtle hover:border-primary/30 transition-all cursor-pointer group space-y-3"
      data-testid={`campaign-card-${camp.campaign_id}`}
      onClick={() => onView(camp)}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium text-text-heading leading-snug line-clamp-2 flex-1">{camp.name}</h4>
        <span className={`flex-shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${ch.bg} ${ch.color}`}>
          <ChIcon className="w-2.5 h-2.5" />
          {ch.label}
        </span>
      </div>

      {/* Preview */}
      {camp.message && (
        <p className="text-xs text-text-muted line-clamp-2 leading-relaxed">{camp.message}</p>
      )}

      {/* Tags */}
      {camp.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {camp.tags.slice(0, 3).map(t => (
            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{t}</span>
          ))}
          {camp.tags.length > 3 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-text-muted">+{camp.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="flex items-center justify-between text-[10px] text-text-muted">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" /> {camp.audience_count || 0}
          </span>
          {sent && (
            <>
              <span className="text-emerald-400">{camp.sent || 0} enviados</span>
              {camp.failed > 0 && <span className="text-red-400">{camp.failed} fallidos</span>}
            </>
          )}
        </div>
        <span>{camp.created_at ? new Date(camp.created_at).toLocaleDateString('es') : ''}</span>
      </div>

      {/* Actions */}
      {!sent && !sending && (
        <button
          onClick={(e) => { e.stopPropagation(); onLaunch(camp); }}
          className="w-full py-2 rounded-xl bg-primary/10 border border-primary/20 text-primary text-xs font-medium hover:bg-primary/20 transition-all flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100"
          data-testid={`launch-camp-${camp.campaign_id}`}
        >
          <Send className="w-3.5 h-3.5" /> Lanzar ahora
        </button>
      )}
      {sending && (
        <div className="flex items-center gap-2 text-xs text-amber-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando...
        </div>
      )}
      {sent && (
        <div className="flex items-center gap-2 text-xs text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" /> Completada
        </div>
      )}
    </div>
  );
}

// missing import fix
const Users = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

/* ── New Campaign Modal ──────────────────────────────────────────────────────── */
function NewCampaignModal({ onClose, onCreated, authFetch }) {
  const [form, setForm] = useState({
    name: '', channel: 'whatsapp', message: '', subject: '', tags: '', only_consent: true
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStyle, setAiStyle] = useState('visual');

  const generateAI = async () => {
    if (!form.name) { toast.error('Ingresa un nombre de campaña para generar el mensaje'); return; }
    setAiLoading(true);
    try {
      const r = await authFetch(`${API}/crm/campaigns/generate-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nicho: form.name, canal: form.channel }),
      });
      const j = await r.json();
      const variant = j.messages?.[aiStyle] || j.messages?.visual || {};
      const text = variant.texto || variant.text || '';
      if (text) {
        setForm(p => ({ ...p, message: text }));
        toast.success('Mensaje generado con IA y PNL');
      }
    } catch (e) { toast.error(e.message); }
    setAiLoading(false);
  };

  const save = async () => {
    if (!form.name || !form.message) { toast.error('Nombre y mensaje son obligatorios'); return; }
    setSaving(true);
    try {
      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const r = await authFetch(`${API}/crm/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tags }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al crear');
      toast.success('Campaña creada');
      onCreated?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-3xl border border-border-subtle bg-[#060D1F] shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-text-heading">Nueva campaña</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-text-muted hover:text-text-heading">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <input type="text" placeholder="Nombre de la campaña" value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="input-base w-full" data-testid="new-camp-name" />

          <div className="flex gap-2">
            {Object.entries(CHANNEL_CONFIG).map(([k, v]) => (
              <button key={k} onClick={() => setForm(p => ({ ...p, channel: k }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${form.channel === k ? `${v.bg} ${v.color} border border-current` : 'bg-card border border-border-subtle text-text-muted'}`}>
                <v.icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            ))}
          </div>

          <input type="text" placeholder="Tags/países separados por coma (ej: peru, colombia)"
            value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
            className="input-base w-full" />

          {form.channel === 'email' && (
            <input type="text" placeholder="Asunto del email" value={form.subject}
              onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
              className="input-base w-full" />
          )}

          <div className="flex items-center justify-between gap-2">
            <label className="text-xs text-text-muted">Mensaje</label>
            <div className="flex items-center gap-2">
              <select value={aiStyle} onChange={e => setAiStyle(e.target.value)} className="input-base text-xs py-1 px-2 w-auto">
                <option value="visual">Visual</option>
                <option value="auditivo">Auditivo</option>
                <option value="kinestesico">Kinestésico</option>
              </select>
              <button onClick={generateAI} disabled={aiLoading}
                className="btn-primary text-xs py-1 px-3 flex items-center gap-1" data-testid="kanban-gen-ai">
                <Sparkles className="w-3 h-3" /> {aiLoading ? '...' : 'Generar IA'}
              </button>
            </div>
          </div>
          <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
            rows={5} placeholder="Mensaje de la campaña..."
            className="input-base w-full resize-none text-sm" data-testid="new-camp-message" />
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm py-2 px-6" data-testid="new-camp-save">
            {saving ? 'Creando...' : 'Crear campaña'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Campaign detail modal ───────────────────────────────────────────────── */
function CampaignDetailModal({ camp, onClose, onLaunch }) {
  if (!camp) return null;
  const ch = CHANNEL_CONFIG[camp.channel] || CHANNEL_CONFIG.social;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-3xl border border-border-subtle bg-[#060D1F] shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-text-heading">{camp.name}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-text-muted hover:text-text-heading">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="glass-card p-3 text-center">
            <p className="text-[10px] text-text-muted uppercase">Canal</p>
            <p className={`text-sm font-bold mt-1 ${ch.color}`}>{ch.label}</p>
          </div>
          <div className="glass-card p-3 text-center">
            <p className="text-[10px] text-text-muted uppercase">Estado</p>
            <p className="text-sm font-bold mt-1 text-text-heading capitalize">{camp.status}</p>
          </div>
          <div className="glass-card p-3 text-center">
            <p className="text-[10px] text-text-muted uppercase">Audiencia</p>
            <p className="text-2xl font-bold mt-1 text-primary">{camp.audience_count || 0}</p>
          </div>
          {camp.sent !== undefined && (
            <div className="glass-card p-3 text-center">
              <p className="text-[10px] text-text-muted uppercase">Enviados</p>
              <p className="text-2xl font-bold mt-1 text-emerald-400">{camp.sent}</p>
            </div>
          )}
        </div>
        {camp.message && (
          <div>
            <p className="text-xs text-text-muted mb-2">Mensaje</p>
            <div className="bg-white/3 rounded-xl p-4 text-sm text-text-body whitespace-pre-wrap">{camp.message}</div>
          </div>
        )}
        {camp.status === 'draft' && (
          <button onClick={() => { onLaunch(camp); onClose(); }}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="camp-detail-launch">
            <Send className="w-4 h-4" /> Lanzar campaña ahora
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main Kanban Board ──────────────────────────────────────────────────────── */
export default function CampaignKanban({ authFetch }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [viewCamp, setViewCamp] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/crm/campaigns?limit=100`);
      const j = await r.json();
      setCampaigns(j.campaigns || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const launch = async (camp) => {
    toast.loading(`Lanzando "${camp.name}"...`, { id: 'launch' });
    try {
      const r = await authFetch(`${API}/crm/campaigns/${camp.campaign_id}/send`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Error al enviar');
      toast.success(`Campaña enviada · ${j.sent} mensajes`, { id: 'launch' });
      load();
    } catch (e) {
      toast.error(e.message, { id: 'launch' });
    }
  };

  const CHANNELS_FILTER = ['all', 'whatsapp', 'email', 'social'];

  const filtered = campaigns.filter(c =>
    filter === 'all' || c.channel === filter
  );

  const forColumn = (col) => filtered.filter(c => col.statuses.includes(c.status));

  return (
    <div className="space-y-5" data-testid="campaign-kanban">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-heading font-bold text-text-heading flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary" />
            </span>
            Centro de Campañas
          </h2>
          <p className="text-text-muted text-sm mt-1">Gestiona y lanza campañas en todos los canales. {campaigns.length} campañas totales.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="btn-secondary text-sm py-2 px-3">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm py-2 px-5 flex items-center gap-2" data-testid="new-campaign-btn">
            <Plus className="w-4 h-4" /> Nueva campaña
          </button>
        </div>
      </div>

      {/* Channel filter */}
      <div className="flex gap-2 flex-wrap">
        {CHANNELS_FILTER.map(ch => {
          const count = ch === 'all' ? campaigns.length : campaigns.filter(c => c.channel === ch).length;
          const cfg = ch === 'all' ? null : CHANNEL_CONFIG[ch];
          return (
            <button key={ch} onClick={() => setFilter(ch)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                filter === ch ? 'bg-primary text-[#0A1A3F] border-primary' : 'bg-card border-border-subtle text-text-muted hover:border-primary/30'
              }`} data-testid={`kanban-filter-${ch}`}>
              {cfg ? <cfg.icon className="w-3.5 h-3.5" /> : <Zap className="w-3.5 h-3.5" />}
              {ch === 'all' ? 'Todos' : cfg?.label || ch}
              <span className={`text-[9px] px-1 rounded-full ${filter === ch ? 'bg-[#0A1A3F]/30' : 'bg-white/10'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Kanban board */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map(col => {
          const cards = forColumn(col);
          const ColIcon = col.icon;
          return (
            <div key={col.id} className={`rounded-2xl border ${col.color} ${col.bg} p-4 space-y-3`} data-testid={`kanban-col-${col.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ColIcon className={`w-4 h-4 ${col.id === 'sent' ? 'text-emerald-400' : col.id === 'sending' ? 'text-amber-400 animate-spin' : 'text-text-muted'}`} />
                  <span className="text-xs font-medium text-text-heading">{col.label}</span>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-text-muted">{cards.length}</span>
              </div>

              <div className="space-y-2">
                {cards.map(c => (
                  <CampaignCard
                    key={c.campaign_id}
                    camp={c}
                    onLaunch={launch}
                    onView={setViewCamp}
                    onRefresh={load}
                  />
                ))}
                {cards.length === 0 && (
                  <div className="py-8 text-center text-text-muted text-xs opacity-50">Sin campañas</div>
                )}
              </div>

              {col.id === 'draft' && (
                <button onClick={() => setShowNew(true)}
                  className="w-full py-2 rounded-xl border border-dashed border-white/10 text-text-muted text-xs hover:border-primary/30 hover:text-primary transition-all flex items-center justify-center gap-1">
                  <Plus className="w-3 h-3" /> Nueva
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showNew && <NewCampaignModal onClose={() => setShowNew(false)} onCreated={load} authFetch={authFetch} />}
      {viewCamp && <CampaignDetailModal camp={viewCamp} onClose={() => setViewCamp(null)} onLaunch={launch} />}
    </div>
  );
}
